import { Link, NavLink, Outlet, useLocation, useParams } from "react-router-dom";

import { useExpenses } from "../api/hooks";
import { AccountMenu } from "./AccountMenu";
import { TabBar } from "./TabBar";
import { PunchClock } from "./PunchClock";

/** Current year from the URL (/year/:year/...), else this calendar year. */
export function useYear(): number {
  const { year } = useParams();
  const y = Number(year);
  return Number.isInteger(y) && y >= 1990 && y <= 2200 ? y : new Date().getFullYear();
}

const YEAR_PAGES = /^\/year\/\d{4}(\/(overview|jobs|hours|doils|settings))?$/;

function YearNav({ year }: { year: number }) {
  const { pathname } = useLocation();
  if (!YEAR_PAGES.test(pathname)) return null;
  const to = (y: number) => pathname.replace(/^\/year\/\d{4}/, `/year/${y}`);
  return (
    <div className="yearnav" aria-label="Year">
      <Link to={to(year - 1)} aria-label="Previous year">
        ‹
      </Link>
      <strong>{year}</strong>
      <Link to={to(year + 1)} aria-label="Next year">
        ›
      </Link>
    </div>
  );
}

export function Layout() {
  const year = useYear();
  const { pathname } = useLocation();
  const jobActive = pathname.startsWith("/job/");
  const expenses = useExpenses();

  return (
    <>
      <header className="bar">
        <Link className="brand" to={`/year/${year}/overview`}>
          <img className="brand-logo" src="/favicon.svg" alt="" width="28" height="28" />
          <b>Work</b> <span>Tracker</span>
        </Link>
        <nav className="topnav" aria-label="Main">
          <NavLink to={`/year/${year}/overview`}>Overview</NavLink>
          <NavLink to={`/year/${year}`} end>
            Planner
          </NavLink>
          <NavLink
            to={`/year/${year}/jobs`}
            className={({ isActive }) => (isActive || jobActive ? "active" : "")}
          >
            {expenses ? <>Jobs &amp; expenses</> : "Jobs"}
          </NavLink>
          <NavLink to={`/year/${year}/hours`}>Hours</NavLink>
          <NavLink to={`/year/${year}/doils`}>DOILs</NavLink>
        </nav>
        <div className="bar-right">
          <PunchClock />
          <YearNav year={year} />
          <AccountMenu year={year} />
        </div>
      </header>
      <main>
        <Outlet />
      </main>
      <TabBar year={year} />
    </>
  );
}

export function Loading() {
  return <p className="muted">Loading…</p>;
}

export function LoadError({ error }: { error: unknown }) {
  return (
    <div className="panel">
      <p>{error instanceof Error ? error.message : "Couldn't load this page."}</p>
    </div>
  );
}

/** Dims a page while it shows the previous year's figures and the chosen year's are loading. */
export function Stale({ on, children }: { on: boolean; children: React.ReactNode }) {
  return (
    <div className={on ? "is-stale" : undefined} aria-busy={on || undefined}>
      {children}
    </div>
  );
}
